import { useState } from "react";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { ExpandIcon } from "./icons";

// Before/after comparison: original photo (itemOne, revealed from the left as the handle
// moves right) vs the rendered/blended result (itemTwo). The handle opens mid-frame (see the
// store's comparePosition default) so both the real house and the new door are visible at once.
// fullscreen: fills its (viewport-sized) container with contain-fit images — both letterbox
// identically (same photo aspect) so the divider stays meaningful edge to edge. onFullscreen is
// omitted there (the expand button would be a no-op inside the fullscreen overlay).
export function CompareView({ before, after, position, onPositionChange, onFullscreen, fullscreen = false }: {
  before: string;
  after: string;
  position: number;
  onPositionChange: (pos: number) => void;
  onFullscreen?: () => void;
  fullscreen?: boolean;
}) {
  const imgStyle = fullscreen ? { width: "100%", height: "100%", objectFit: "contain" as const } : undefined;
  // Photo aspect for the stylesheet (mirrors QuadEditor's --rg-photo-ar): short-viewport CSS caps
  // the in-page slider via width = 62vh × aspect, since a comparison must never be cover-cropped.
  const [ar, setAr] = useState(1.5);
  return (
    <div style={{ position: "relative", width: "100%", height: fullscreen ? "100%" : undefined, ...({ "--rg-photo-ar": ar } as React.CSSProperties) }}>
      <ReactCompareSlider
        position={position}
        onPositionChange={onPositionChange}
        itemOne={
          <ReactCompareSliderImage
            src={before}
            alt="Original"
            style={imgStyle}
            onLoad={(e) => {
              const t = e.currentTarget;
              if (t.naturalWidth > 0 && t.naturalHeight > 0) setAr(t.naturalWidth / t.naturalHeight);
            }}
          />
        }
        itemTwo={<ReactCompareSliderImage src={after} alt="New door" style={imgStyle} />}
        style={fullscreen ? { width: "100%", height: "100%" } : { width: "100%", borderRadius: 8 }}
      />

      <Label side="left">Original</Label>
      {/* Fullscreen: shift the right label clear of the overlay's 44px close button. */}
      <Label side="right" inset={fullscreen ? 72 : 10}>New door</Label>

      {onFullscreen && (
        <button
          type="button"
          title="View full screen"
          aria-label="View full screen"
          className="rg-media-btn"
          onClick={onFullscreen}
          // Bottom-left so it never overlaps the "Original" label in the top-left corner.
          // Size lives on .rg-media-btn: 34px with a mouse, 44px on coarse pointers.
          style={{
            position: "absolute", left: 8, bottom: 8, zIndex: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(15,23,42,0.6)", color: "#fff", border: "none", borderRadius: 8,
            backdropFilter: "blur(4px)", cursor: "pointer",
          }}
        >
          <ExpandIcon size={18} />
        </button>
      )}
    </div>
  );
}

function Label({ side, children, inset = 10 }: { side: "left" | "right"; children: string; inset?: number }) {
  return (
    <span
      style={{
        position: "absolute",
        top: 10,
        ...(side === "left" ? { left: inset } : { right: inset }),
        background: "rgba(15,23,42,0.7)",
        color: "#fff",
        padding: "3px 10px",
        borderRadius: "var(--radius-pill)",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        pointerEvents: "none",
      }}
    >
      {children}
    </span>
  );
}
